import TransitionPlanForm from "./transitionplanform";
import { useEffect, useRef, useState } from "react";
import { Button, CloseButton, Col, Modal, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowRight,
    faBullseye,
    faTrashCan,
    faLayerGroup,
    faPenToSquare,
    faPlus,
    faUserGroup,
    faWandMagicSparkles
} from "@fortawesome/free-solid-svg-icons";
import useApi from "../../../../hooks/use-api";
import NeedsAddEdit from "./needs/needsaddedit";
import GoalsAddEdit from "./goals/goalsaddedit";
import ProviderAddEdit from "./providers/provideraddedit";
import styles from "../../../../styles/transitionplan.module.css";
import ServicesAddEdit from "./services/servicesaddedit";
import { useOutletContext } from "react-router-dom";

const TransitionPlanMain = () => {
    const { ApplicationID } = useOutletContext();
    const [needs, setNeeds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const initialTPFormValues = {
        clinicalsocialsummary: null,
        housingsummary: null,
        plancompleted: null,
        planfortransitionintocommunity: null,
        pretransitionservicesummary: null
    };

    const [transitionPlanForm, setTransitionPlanForm] = useState(initialTPFormValues);
    const { getRequest, deleteRequest } = useApi();
    const editorRef = useRef(null);
    const deleteCloseTimerRef = useRef(null);
    const [plannerFeedback, setPlannerFeedback] = useState(null);
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        level: null,
        label: "",
        endpoint: null,
        deleteObject: null,
        successMessage: "",
        errorMessage: "",
        isSubmitting: false,
        feedbackType: null,
        feedbackMessage: ""
    });

    const initialDeleteModalState = {
        isOpen: false,
        level: null,
        label: "",
        endpoint: null,
        deleteObject: null,
        successMessage: "",
        errorMessage: "",
        isSubmitting: false,
        feedbackType: null,
        feedbackMessage: ""
    };

    const [viewState, setViewState] = useState({
        applicationId: ApplicationID,
        needId: null,
        goalId: null,
        serviceId: null,
        providerId: null,
        mode: "view"
    });

    const selectedNeed = needs.find((need) => need.id === viewState.needId) || null;
    const selectedGoal = selectedNeed?.goals?.find((goal) => goal.id === viewState.goalId) || null;
    const selectedService = selectedGoal?.services?.find((service) => service.id === viewState.serviceId) || null;
    const selectedProvider = selectedService?.providers?.find((provider) => provider.id === viewState.providerId) || null;

    const goals = selectedNeed?.goals || [];
    const services = selectedGoal?.services || [];
    const providers = selectedService?.providers || [];

    const _clearPlannerFeedback = () => setPlannerFeedback(null);
    const _handleBoardCardKeyDown = (event, selectFn) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            selectFn();
        }
    };

    const _selectNeed = (needId) => {
        _clearPlannerFeedback();
        setViewState((prev) => ({
            ...prev,
            needId,
            goalId: null,
            serviceId: null,
            providerId: null,
            mode: "view"
        }));
    };

    const _selectGoal = (goalId) => {
        _clearPlannerFeedback();
        setViewState((prev) => ({
            ...prev,
            goalId,
            serviceId: null,
            providerId: null,
            mode: "view"
        }));
    };

    const _selectService = (serviceId) => {
        _clearPlannerFeedback();
        setViewState((prev) => ({
            ...prev,
            serviceId,
            providerId: null,
            mode: "view"
        }));
    };

    const _selectProvider = (providerId) => {
        _clearPlannerFeedback();
        setViewState((prev) => ({
            ...prev,
            providerId,
            mode: "view"
        }));
    };

    const _clearEditor = (options = {}) => {
        const { preservePlannerFeedback = false } = options;

        if (!preservePlannerFeedback) {
            _clearPlannerFeedback();
        }

        setViewState((prev) => ({
            ...prev,
            mode: "view"
        }));
    };

    const _setEditorState = (nextState) => {
        _clearPlannerFeedback();
        setViewState((prev) => ({
            ...prev,
            ...nextState
        }));
    };

    const _onNeedAdd = () => _setEditorState({ needId: null, goalId: null, serviceId: null, providerId: null, mode: "add" });
    const _onNeedEdit = (needId) => _setEditorState({ needId, goalId: null, serviceId: null, providerId: null, mode: "edit" });
    const _onGoalAdd = () => _setEditorState({ goalId: null, serviceId: null, providerId: null, mode: "add" });
    const _onGoalEdit = (goalId) => _setEditorState({ goalId, serviceId: null, providerId: null, mode: "edit" });
    const _onServiceAdd = () => _setEditorState({ serviceId: null, providerId: null, mode: "add" });
    const _onServiceEdit = (serviceId) => _setEditorState({ serviceId, providerId: null, mode: "edit" });
    const _onProviderAdd = () => _setEditorState({ providerId: null, mode: "add" });
    const _onProviderEdit = (providerId) => _setEditorState({ providerId, mode: "edit" });

    const _refreshLists = async (options = {}) => {
        const { preserveEditorViewport = false } = options;
        const editorTopBeforeRefresh = preserveEditorViewport && editorRef.current
            ? editorRef.current.getBoundingClientRect().top
            : null;

        setIsRefreshing(true);
        const [data, status] = await getRequest("TransitionPlan", { ApplicationID });

        if (status === 200) {
            setNeeds(data.needs);
        }

        setIsRefreshing(false);

        if (preserveEditorViewport && editorRef.current !== null && editorTopBeforeRefresh !== null) {
            window.requestAnimationFrame(() => {
                const editorTopAfterRefresh = editorRef.current?.getBoundingClientRect().top;

                if (editorTopAfterRefresh === undefined || editorTopAfterRefresh === null) {
                    return;
                }

                const scrollAdjustment = editorTopAfterRefresh - editorTopBeforeRefresh;

                if (scrollAdjustment !== 0) {
                    window.scrollBy({
                        top: scrollAdjustment,
                        left: 0,
                        behavior: "auto"
                    });
                }
            });
        }
    };

    const _resetFormValues = (values) => {
        setTransitionPlanForm(values);
    };

    const _renderValue = (value, fallback = "Not entered") => {
        if (value === null || value === undefined || value === "") {
            return <span className={styles.boardValueMuted}>{fallback}</span>;
        }

        return <span>{value}</span>;
    };

    const _getRequestedAmount = (provider) => provider.requestedamount || provider.homemodsupplementrequestamount || "";
    const _getDisplayText = (value, fallback) => value === null || value === undefined || value === "" ? fallback : value;
    const _getNeedLabel = (need) => _getDisplayText(need?.needtitle, "Untitled Need");
    const _getGoalLabel = (goal) => _getDisplayText(goal?.goal, "Untitled Goal");
    const _getServiceLabel = (service) => _getDisplayText(service?.servicetype, "Untitled Service");
    const _getProviderLabel = (provider) => _getDisplayText(provider?.name, "Unnamed Provider");

    const _getDeleteConfig = ({ level, ids, label }) => {
        return {
            need: {
                endpoint: "DeleteNeed",
                deleteObject: { ApplicationID, NeedID: ids.needId },
                label,
                success: "The Need was successfully deleted!",
                error: "An error occurred when deleting the Need."
            },
            goal: {
                endpoint: "DeleteGoal",
                deleteObject: { ApplicationID, NeedID: ids.needId, GoalID: ids.goalId },
                label,
                success: "The Goal was successfully deleted!",
                error: "An error occurred when deleting the Goal."
            },
            service: {
                endpoint: "DeleteService",
                deleteObject: { ApplicationID, NeedID: ids.needId, GoalID: ids.goalId, ServiceID: ids.serviceId },
                label,
                success: "The Service was successfully deleted!",
                error: "An error occurred when deleting the Service."
            },
            provider: {
                endpoint: "DeleteProvider",
                deleteObject: { ApplicationID, NeedID: ids.needId, GoalID: ids.goalId, ServiceID: ids.serviceId, ProviderID: ids.providerId },
                label,
                success: "The Provider was successfully deleted!",
                error: "An error occurred when deleting the Provider."
            }
        }[level];
    };

    const _openDeleteModal = (options) => {
        const deleteConfig = _getDeleteConfig(options);

        setDeleteModal({
            isOpen: true,
            level: options.level,
            label: deleteConfig.label,
            endpoint: deleteConfig.endpoint,
            deleteObject: deleteConfig.deleteObject,
            successMessage: deleteConfig.success,
            errorMessage: deleteConfig.error,
            isSubmitting: false,
            feedbackType: null,
            feedbackMessage: ""
        });
    };

    const _closeDeleteModal = () => {
        if (deleteCloseTimerRef.current) {
            window.clearTimeout(deleteCloseTimerRef.current);
            deleteCloseTimerRef.current = null;
        }

        setDeleteModal((prev) => ({
            ...prev,
            isOpen: false
        }));
    };

    const _resetDeleteModal = () => {
        setDeleteModal(initialDeleteModalState);
    };

    const _confirmDelete = async () => {
        setPlannerFeedback(null);
        setDeleteModal((prev) => ({
            ...prev,
            isSubmitting: true,
            feedbackType: null,
            feedbackMessage: ""
        }));

        const [, status] = await deleteRequest(deleteModal.endpoint, deleteModal.deleteObject);

        if (status === 200) {
            setPlannerFeedback({
                type: "success",
                message: deleteModal.successMessage
            });
            setDeleteModal((prev) => ({
                ...prev,
                isSubmitting: false,
                feedbackType: "success",
                feedbackMessage: deleteModal.successMessage
            }));
            _refreshLists();
            _clearEditor({ preservePlannerFeedback: true });
            deleteCloseTimerRef.current = window.setTimeout(() => {
                _closeDeleteModal();
            }, 900);
        } else {
            setPlannerFeedback({
                type: "error",
                message: deleteModal.errorMessage
            });
            setDeleteModal((prev) => ({
                ...prev,
                isSubmitting: false,
                feedbackType: "error",
                feedbackMessage: deleteModal.errorMessage
            }));
        }
    };

    const ContextDeleteButton = ({ onClick, label, srLabel }) => {
        return (
            <button type="button" className={styles.contextDeleteButton} onClick={onClick} aria-label={srLabel || label}>
                <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
                <span>{label}</span>
            </button>
        );
    };

    const _getEditorTitle = () => {
        if (viewState.mode !== "add" && viewState.mode !== "edit") return "Nothing selected";
        if (viewState.providerId) return viewState.mode === "edit" ? "Edit Provider" : "Add Provider";
        if (viewState.serviceId) return viewState.mode === "edit" ? "Edit Service" : "Add Provider";
        if (viewState.goalId) return viewState.mode === "edit" ? "Edit Goal" : "Add Service";
        if (viewState.needId) return viewState.mode === "edit" ? "Edit Need" : "Add Goal";
        return "Add Need";
    };

    const _getEditorDescription = () => {
        if (viewState.mode !== "add" && viewState.mode !== "edit") {
            return "Select an item from the planner board or create something new.";
        }

        if (viewState.providerId) return "Update the selected provider details.";
        if (viewState.serviceId && viewState.mode === "edit") return "Update the selected service details.";
        if (viewState.serviceId && viewState.mode === "add") return "Add a provider under the selected service.";
        if (viewState.goalId && viewState.mode === "edit") return "Update the selected goal.";
        if (viewState.goalId && viewState.mode === "add") return "Add a service under the selected goal.";
        if (viewState.needId && viewState.mode === "edit") return "Update the selected need.";
        if (viewState.needId && viewState.mode === "add") return "Add a goal under the selected need.";
        return "Create a new need to start the transition plan.";
    };

    const _renderEditor = () => {
        if (viewState.mode === "add") {
            if (!viewState.needId) {
                return <NeedsAddEdit key="need-add" ApplicationId={ApplicationID} NeedId={null} ReturnFunction={_clearEditor} RefreshFunction={_refreshLists} />;
            }

            if (!viewState.goalId) {
                return <GoalsAddEdit key={`goal-add-${viewState.needId}`} ReturnFunction={_clearEditor} ApplicationID={ApplicationID} NeedID={viewState.needId} GoalID={null} RefreshFunction={_refreshLists} />;
            }

            if (!viewState.serviceId) {
                return <ServicesAddEdit key={`service-add-${viewState.needId}-${viewState.goalId}`} ApplicationID={ApplicationID} NeedID={viewState.needId} GoalID={viewState.goalId} ServiceID={null} ReturnFunction={_clearEditor} RefreshFunction={_refreshLists} />;
            }

            return <ProviderAddEdit key={`provider-add-${viewState.needId}-${viewState.goalId}-${viewState.serviceId}`} ApplicationID={ApplicationID} NeedID={viewState.needId} GoalID={viewState.goalId} ServiceID={viewState.serviceId} ProviderID={null} ReturnFunction={_clearEditor} RefreshFunction={_refreshLists} />;
        }

        if (viewState.mode === "edit") {
            if (viewState.providerId) {
                return <ProviderAddEdit key={`provider-edit-${viewState.providerId}`} ApplicationID={ApplicationID} NeedID={viewState.needId} GoalID={viewState.goalId} ServiceID={viewState.serviceId} ProviderID={viewState.providerId} ReturnFunction={_clearEditor} RefreshFunction={_refreshLists} />;
            }

            if (viewState.serviceId) {
                return <ServicesAddEdit key={`service-edit-${viewState.serviceId}`} ApplicationID={ApplicationID} NeedID={viewState.needId} GoalID={viewState.goalId} ServiceID={viewState.serviceId} ReturnFunction={_clearEditor} RefreshFunction={_refreshLists} />;
            }

            if (viewState.goalId) {
                return <GoalsAddEdit key={`goal-edit-${viewState.goalId}`} ReturnFunction={_clearEditor} ApplicationID={ApplicationID} NeedID={viewState.needId} GoalID={viewState.goalId} RefreshFunction={_refreshLists} />;
            }

            if (viewState.needId) {
                return <NeedsAddEdit key={`need-edit-${viewState.needId}`} ApplicationId={ApplicationID} NeedId={viewState.needId} ReturnFunction={_clearEditor} RefreshFunction={_refreshLists} />;
            }
        }

        return (
            <div className={styles.boardEmptyEditor}>
                <div className={styles.boardEmptyEditorIcon}>
                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                </div>
                <h4 className="mb-2">Choose where to work</h4>
                <p className="mb-0">Select a Need, Goal, Service, or Provider from the board, or use the add buttons to create a new item in context.</p>
            </div>
        );
    };

    const totalGoals = needs.reduce((sum, need) => sum + (need.goals?.length || 0), 0);
    const totalServices = needs.reduce((sum, need) => sum + (need.goals || []).reduce((goalSum, goal) => goalSum + (goal.services?.length || 0), 0), 0);
    const totalProviders = needs.reduce((sum, need) => sum + (need.goals || []).reduce((goalSum, goal) => goalSum + (goal.services || []).reduce((serviceSum, service) => serviceSum + (service.providers?.length || 0), 0), 0), 0);

    useEffect(() => {
        const loadTransitionPlan = async () => {
            setIsLoading(true);
            const [data, status] = await getRequest("TransitionPlan", { ApplicationID });

            if (status === 200) {
                setTransitionPlanForm({
                    clinicalsocialsummary: data.clinicalsocialsummary === null ? "" : data.clinicalsocialsummary,
                    housingsummary: data.housingsummary === null ? "" : data.housingsummary,
                    plancompleted: data.plancompleted === null ? "" : data.plancompleted,
                    planfortransitionintocommunity: data.planfortransitionintocommunity === null ? "" : data.planfortransitionintocommunity,
                    pretransitionservicesummary: data.pretransitionservicesummary === null ? "" : data.pretransitionservicesummary
                });
                setNeeds(data.needs);
            }

            setIsLoading(false);
        };

        loadTransitionPlan();
    }, [ApplicationID]);

    useEffect(() => {
        return () => {
            if (deleteCloseTimerRef.current) {
                window.clearTimeout(deleteCloseTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (viewState.mode !== "add" && viewState.mode !== "edit") {
            return;
        }

        const scrollTimer = window.setTimeout(() => {
            editorRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
            editorRef.current?.focus({ preventScroll: true });
        }, 50);

        return () => window.clearTimeout(scrollTimer);
    }, [viewState.mode, viewState.needId, viewState.goalId, viewState.serviceId, viewState.providerId]);

    if (isLoading) {
        return <div className={styles.statusMessage} role="status" aria-live="polite">Loading transition plan...</div>;
    }

    return (
        <>
            <Row className={styles.transitionPlanLayout}>
                <Col xs={12}>
                    <TransitionPlanForm FormValues={transitionPlanForm} ApplicationID={ApplicationID} ResetFormValues={_resetFormValues} />
                </Col>

                <Col xs={12} className="mt-3">
                    <div className={`clientCard ${styles.workspaceShell}`}>
                    <div className={styles.workspaceIntro}>
                        <div>
                            <div className={styles.workspaceEyebrow}>Transition plan planner</div>
                            <h2 className={styles.workspaceTitle} id="transition-plan-planner-title">Build the plan from left to right</h2>
                            <p className={styles.workspaceDescription}>
                                Needs lead to goals, goals lead to services, and services lead to providers. Pick a column, keep your place, and use the editor below for changes.
                            </p>
                        </div>
                        <div className={styles.workspaceMeta}>
                            {isRefreshing && (
                                <span className={styles.refreshingIndicator} role="status" aria-live="polite">
                                    <Spinner animation="border" size="sm" aria-hidden="true" />
                                    Refreshing
                                </span>
                            )}
                        </div>
                    </div>

                    {plannerFeedback && (
                        <div className={`${styles.inlineFeedback} ${plannerFeedback.type === "error" ? styles.inlineFeedbackError : styles.inlineFeedbackSuccess}`} role="status" aria-live="polite">
                            {plannerFeedback.message}
                        </div>
                    )}

                    <div className={styles.workspaceStats} aria-label="Transition plan totals">
                        <div className={styles.workspaceStatCard}><span className={styles.workspaceStatLabel}>Needs</span><strong className={styles.workspaceStatValue}>{needs.length}</strong></div>
                        <div className={styles.workspaceStatCard}><span className={styles.workspaceStatLabel}>Goals</span><strong className={styles.workspaceStatValue}>{totalGoals}</strong></div>
                        <div className={styles.workspaceStatCard}><span className={styles.workspaceStatLabel}>Services</span><strong className={styles.workspaceStatValue}>{totalServices}</strong></div>
                        <div className={styles.workspaceStatCard}><span className={styles.workspaceStatLabel}>Providers</span><strong className={styles.workspaceStatValue}>{totalProviders}</strong></div>
                    </div>

                    <div className={styles.boardContextBar} role="status" aria-live="polite" aria-atomic="true">
                        <span className={styles.boardContextPill}>Need: {_renderValue(selectedNeed?.needtitle, "Choose a need")}</span>
                        <span className={styles.boardContextPill}>Goal: {_renderValue(selectedGoal?.goal, "Choose a goal")}</span>
                        <span className={styles.boardContextPill}>Service: {_renderValue(selectedService?.servicetype, "Choose a service")}</span>
                        <span className={styles.boardContextPill}>Provider: {_renderValue(selectedProvider?.name, "Optional")}</span>
                    </div>

                    <div className={styles.boardColumns} aria-labelledby="transition-plan-planner-title">
                        <section className={styles.boardColumn} aria-labelledby="transition-plan-needs-heading">
                            <div className={styles.boardColumnHeader}>
                                <div>
                                    <div className={styles.boardColumnEyebrow}>Step 1</div>
                                    <h3 className={styles.boardColumnTitle} id="transition-plan-needs-heading">Needs</h3>
                                </div>
                                <Button type="button" size="sm" variant="success" onClick={_onNeedAdd} aria-label="Add need" title="Add need">
                                    <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
                                    <span className="visually-hidden">Add need</span>
                                </Button>
                            </div>
                            <div className={styles.boardColumnBody}>
                                {!needs.length && <div className={styles.boardEmptyState}>No needs yet.</div>}
                                {needs.map((need) => (
                                    <div
                                        key={need.id}
                                        className={`${styles.boardItemCard} ${viewState.needId === need.id ? styles.boardItemCardActive : ""}`}
                                        onClick={() => _selectNeed(need.id)}
                                        onKeyDown={(event) => _handleBoardCardKeyDown(event, () => _selectNeed(need.id))}
                                        role="button"
                                        tabIndex={0}
                                        aria-pressed={viewState.needId === need.id}
                                        aria-label={`Select need ${_getNeedLabel(need)}`}
                                    >
                                        <div className={styles.boardItemTop}>
                                            <div className={styles.boardItemIcon} aria-hidden="true"><FontAwesomeIcon icon={faLayerGroup} /></div>
                                        </div>
                                        <div className={styles.boardItemContent}>
                                            <strong>{_getNeedLabel(need)}</strong>
                                            <span className={styles.boardItemMeta}>{need.needtype || "Type not entered"}</span>
                                            <span className={styles.boardItemMeta}>{need.goals?.length || 0} goals</span>
                                        </div>
                                        {viewState.needId === need.id && (
                                            <span className={styles.boardItemSelectionBar}>
                                                <span className={styles.boardItemSelectionLabel}>Selected need</span>
                                                <span className={styles.boardItemActions} onClick={(event) => event.stopPropagation()}>
                                                    <Button type="button" size="sm" variant="outline-primary" onClick={() => _onNeedEdit(need.id)} aria-label={`Edit need ${_getNeedLabel(need)}`}>
                                                        <FontAwesomeIcon icon={faPenToSquare} className="me-1" aria-hidden="true" />
                                                        Edit
                                                    </Button>
                                                    <ContextDeleteButton onClick={() => _openDeleteModal({ level: "need", ids: { needId: need.id }, label: "Need" })} label="Delete" srLabel={`Delete need ${_getNeedLabel(need)}`} />
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className={styles.boardColumn} aria-labelledby="transition-plan-goals-heading">
                            <div className={styles.boardColumnHeader}>
                                <div>
                                    <div className={styles.boardColumnEyebrow}>Step 2</div>
                                    <h3 className={styles.boardColumnTitle} id="transition-plan-goals-heading">Goals</h3>
                                </div>
                                <Button type="button" size="sm" variant="success" onClick={_onGoalAdd} disabled={!selectedNeed} aria-label="Add goal" title="Add goal">
                                    <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
                                    <span className="visually-hidden">Add goal</span>
                                </Button>
                            </div>
                            <div className={styles.boardColumnBody}>
                                {!selectedNeed && <div className={styles.boardEmptyState}>Select a need to see its goals.</div>}
                                {selectedNeed && !goals.length && <div className={styles.boardEmptyState}>No goals for this need yet.</div>}
                                {goals.map((goal) => (
                                    <div key={goal.id} className={`${styles.boardItemCard} ${viewState.goalId === goal.id ? styles.boardItemCardActive : ""}`} onClick={() => _selectGoal(goal.id)} onKeyDown={(event) => _handleBoardCardKeyDown(event, () => _selectGoal(goal.id))} role="button" tabIndex={0} aria-pressed={viewState.goalId === goal.id} aria-label={`Select goal ${_getGoalLabel(goal)}`}>
                                        <div className={styles.boardItemTop}>
                                            <div className={`${styles.boardItemIcon} ${styles.goalIconBadge}`} aria-hidden="true"><FontAwesomeIcon icon={faBullseye} /></div>
                                        </div>
                                        <div className={styles.boardItemContent}>
                                            <strong>{_getGoalLabel(goal)}</strong>
                                            <span className={styles.boardItemMeta}>{goal.services?.length || 0} services</span>
                                            <span className={styles.boardItemMeta}>{goal.comment || "No comment"}</span>
                                        </div>
                                        {viewState.goalId === goal.id && (
                                            <span className={styles.boardItemSelectionBar}>
                                                <span className={styles.boardItemSelectionLabel}>Selected goal</span>
                                                <span className={styles.boardItemActions} onClick={(event) => event.stopPropagation()}>
                                                    <Button type="button" size="sm" variant="outline-primary" onClick={() => _onGoalEdit(goal.id)} aria-label={`Edit goal ${_getGoalLabel(goal)}`}>
                                                        <FontAwesomeIcon icon={faPenToSquare} className="me-1" aria-hidden="true" />
                                                        Edit
                                                    </Button>
                                                    <ContextDeleteButton onClick={() => _openDeleteModal({ level: "goal", ids: { needId: selectedNeed.id, goalId: goal.id }, label: "Goal" })} label="Delete" srLabel={`Delete goal ${_getGoalLabel(goal)}`} />
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className={styles.boardColumn} aria-labelledby="transition-plan-services-heading">
                            <div className={styles.boardColumnHeader}>
                                <div>
                                    <div className={styles.boardColumnEyebrow}>Step 3</div>
                                    <h3 className={styles.boardColumnTitle} id="transition-plan-services-heading">Services</h3>
                                </div>
                                <Button type="button" size="sm" variant="success" onClick={_onServiceAdd} disabled={!selectedGoal} aria-label="Add service" title="Add service">
                                    <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
                                    <span className="visually-hidden">Add service</span>
                                </Button>
                            </div>
                            <div className={styles.boardColumnBody}>
                                {!selectedGoal && <div className={styles.boardEmptyState}>Select a goal to see its services.</div>}
                                {selectedGoal && !services.length && <div className={styles.boardEmptyState}>No services for this goal yet.</div>}
                                {services.map((service) => (
                                    <div key={service.id} className={`${styles.boardItemCard} ${viewState.serviceId === service.id ? styles.boardItemCardActive : ""}`} onClick={() => _selectService(service.id)} onKeyDown={(event) => _handleBoardCardKeyDown(event, () => _selectService(service.id))} role="button" tabIndex={0} aria-pressed={viewState.serviceId === service.id} aria-label={`Select service ${_getServiceLabel(service)}`}>
                                        <div className={styles.boardItemTop}>
                                            <div className={`${styles.boardItemIcon} ${styles.serviceIconBadge}`} aria-hidden="true"><FontAwesomeIcon icon={faLayerGroup} /></div>
                                        </div>
                                        <div className={styles.boardItemContent}>
                                            <strong>{_getServiceLabel(service)}</strong>
                                            <span className={styles.boardItemMeta}>{service.providers?.length || 0} providers</span>
                                            <span className={styles.boardItemMeta}>{service.comments || "No comment"}</span>
                                        </div>
                                        {viewState.serviceId === service.id && (
                                            <span className={styles.boardItemSelectionBar}>
                                                <span className={styles.boardItemSelectionLabel}>Selected service</span>
                                                <span className={styles.boardItemActions} onClick={(event) => event.stopPropagation()}>
                                                    <Button type="button" size="sm" variant="outline-primary" onClick={() => _onServiceEdit(service.id)} aria-label={`Edit service ${_getServiceLabel(service)}`}>
                                                        <FontAwesomeIcon icon={faPenToSquare} className="me-1" aria-hidden="true" />
                                                        Edit
                                                    </Button>
                                                    <ContextDeleteButton onClick={() => _openDeleteModal({ level: "service", ids: { needId: selectedNeed.id, goalId: selectedGoal.id, serviceId: service.id }, label: "Service" })} label="Delete" srLabel={`Delete service ${_getServiceLabel(service)}`} />
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className={styles.boardColumn} aria-labelledby="transition-plan-providers-heading">
                            <div className={styles.boardColumnHeader}>
                                <div>
                                    <div className={styles.boardColumnEyebrow}>Step 4</div>
                                    <h3 className={styles.boardColumnTitle} id="transition-plan-providers-heading">Providers</h3>
                                </div>
                                <Button type="button" size="sm" variant="success" onClick={_onProviderAdd} disabled={!selectedService} aria-label="Add provider" title="Add provider">
                                    <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
                                    <span className="visually-hidden">Add provider</span>
                                </Button>
                            </div>
                            <div className={styles.boardColumnBody}>
                                {!selectedService && <div className={styles.boardEmptyState}>Select a service to see its providers.</div>}
                                {selectedService && !providers.length && <div className={styles.boardEmptyState}>No providers for this service yet.</div>}
                                {providers.map((provider) => (
                                    <div key={provider.id} className={`${styles.boardItemCard} ${viewState.providerId === provider.id ? styles.boardItemCardActive : ""}`} onClick={() => _selectProvider(provider.id)} onKeyDown={(event) => _handleBoardCardKeyDown(event, () => _selectProvider(provider.id))} role="button" tabIndex={0} aria-pressed={viewState.providerId === provider.id} aria-label={`Select provider ${_getProviderLabel(provider)}`}>
                                        <div className={styles.boardItemTop}>
                                            <div className={`${styles.boardItemIcon} ${styles.providerIconBadge}`} aria-hidden="true"><FontAwesomeIcon icon={faUserGroup} /></div>
                                        </div>
                                        <div className={styles.boardItemContent}>
                                            <strong>{_getProviderLabel(provider)}</strong>
                                            <span className={styles.boardItemMeta}>{provider.providertype || "Provider type not entered"}</span>
                                            <span className={styles.boardItemMeta}>{_getRequestedAmount(provider) || "No amount entered"}</span>
                                        </div>
                                        {viewState.providerId === provider.id && (
                                            <span className={styles.boardItemSelectionBar}>
                                                <span className={styles.boardItemSelectionLabel}>Selected provider</span>
                                                <span className={styles.boardItemActions} onClick={(event) => event.stopPropagation()}>
                                                    <Button type="button" size="sm" variant="outline-primary" onClick={() => _onProviderEdit(provider.id)} aria-label={`Edit provider ${_getProviderLabel(provider)}`}>
                                                        <FontAwesomeIcon icon={faPenToSquare} className="me-1" aria-hidden="true" />
                                                        Edit
                                                    </Button>
                                                    <ContextDeleteButton onClick={() => _openDeleteModal({ level: "provider", ids: { needId: selectedNeed.id, goalId: selectedGoal.id, serviceId: selectedService.id, providerId: provider.id }, label: "Provider" })} label="Delete" srLabel={`Delete provider ${_getProviderLabel(provider)}`} />
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    <div className={styles.editorShell} ref={editorRef} tabIndex={-1} aria-labelledby="transition-plan-editor-title" aria-live="polite">
                        <div className={styles.editorHeader}>
                            <div>
                                <div className={styles.workspaceEyebrow}>Editor</div>
                                <h3 className={styles.editorTitle} id="transition-plan-editor-title">{_getEditorTitle()}</h3>
                                <p className={styles.editorDescription}>{_getEditorDescription()}</p>
                            </div>
                            {viewState.mode === "add" || viewState.mode === "edit" ? (
                                <button type="button" className={styles.editorCloseButton} onClick={_clearEditor} aria-label="Close editor">
                                    <CloseButton aria-hidden="true" />
                                    <span>Close</span>
                                </button>
                            ) : (
                                <div className={styles.editorHint}>
                                    <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                                    <span>Pick context above, edit below</span>
                                </div>
                            )}
                        </div>
                        {_renderEditor()}
                    </div>
                </div>
                </Col>
            </Row>

            <Modal centered show={deleteModal.isOpen} onHide={deleteModal.isSubmitting ? undefined : _closeDeleteModal} onExited={_resetDeleteModal} aria-labelledby="transition-plan-delete-title" aria-describedby="transition-plan-delete-description">
                <Modal.Header closeButton={!deleteModal.isSubmitting}>
                    <Modal.Title id="transition-plan-delete-title">Delete {deleteModal.label}</Modal.Title>
                </Modal.Header>
                <Modal.Body id="transition-plan-delete-description">
                    <p className="mb-2">This will permanently remove the selected {deleteModal.label?.toLowerCase()} from the transition plan.</p>
                    <p className="mb-0 text-muted">This action cannot be undone.</p>
                    {deleteModal.feedbackMessage && (
                        <div className={`${styles.inlineFeedback} ${deleteModal.feedbackType === "error" ? styles.inlineFeedbackError : styles.inlineFeedbackSuccess} mt-3`} role="status" aria-live="polite">
                            {deleteModal.feedbackMessage}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" variant="outline-secondary" onClick={_closeDeleteModal} disabled={deleteModal.isSubmitting}>
                        Cancel
                    </Button>
                    <button type="button" className={styles.modalDeleteButton} onClick={_confirmDelete} disabled={deleteModal.isSubmitting || deleteModal.feedbackType === "success"} aria-label={`Confirm delete ${deleteModal.label}`}>
                        {deleteModal.isSubmitting ? <Spinner animation="border" size="sm" aria-hidden="true" /> : <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />}
                        <span>{deleteModal.isSubmitting ? "Deleting..." : deleteModal.feedbackType === "success" ? "Deleted" : `Delete ${deleteModal.label}`}</span>
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default TransitionPlanMain;
